import { FC } from 'react';
import { Box, ClickAwayListener, Collapse, Paper, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Badge } from '@components';
import { useDirection } from '@hooks';
import { AssessmentDomainStatus } from '@libs/constants';
import { Trans } from '@libs/i18n';
import { CaretRightIcon, CheckIcon } from '@phosphor-icons/react';

import { ProgressFlowItem } from './ProgressFlow';

interface ProgressFlowCongestedBlockProps {
  domains: ProgressFlowItem[];
  startIndex: number;
  isCompleted: boolean;
  blockId: string;
  domainStates: Map<number, AssessmentDomainStatus>;
  domainOptionalMap?: Map<number, boolean>;
  selectedIndex?: number;
  currentDomainIndex: number;
  isExpanded: boolean;
  isNavigationDisabled?: boolean;
  onToggle: () => void;
  onNodeClick?: (index: number) => void;
}

export const ProgressFlowCongestedBlock: FC<ProgressFlowCongestedBlockProps> = observer(
  ({
    domains,
    startIndex,
    isCompleted: _isCompleted,
    blockId: _blockId,
    domainStates,
    domainOptionalMap: _domainOptionalMap,
    selectedIndex,
    currentDomainIndex,
    isExpanded,
    isNavigationDisabled,
    onToggle,
    onNodeClick,
  }) => {
    const { isRtl } = useDirection();
    const domainCount = domains.length;
    const isFuture = startIndex > currentDomainIndex;

    // Check if ALL domains in the block are completed
    const allDomainsCompleted = domains.every((_, idx) => {
      const domainIndex = startIndex + idx;
      const domainState = domainStates.get(domainIndex);
      return domainState === AssessmentDomainStatus.COMPLETED;
    });

    // Only show blue if ALL domains are completed, not just the block status
    const shouldShowBlue = allDomainsCompleted;
    const allDomainsNotStarted = domains.every((_, idx) => {
      const domainIndex = startIndex + idx;
      const domainState = domainStates.get(domainIndex);
      return domainState === AssessmentDomainStatus.NOT_STARTED;
    });
    const shouldShowGrey = allDomainsNotStarted;

    // Handle click away - only close if expanded
    const handleClickAway = () => {
      if (isExpanded) {
        onToggle();
      }
    };

    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box
          sx={{
            position: 'relative',
            flexShrink: 0,
            px: 0.25,
            opacity: isNavigationDisabled ? 0.85 : 1,
            transition: 'opacity 0.2s',
          }}
        >
          <Box
            onClick={isNavigationDisabled ? undefined : onToggle}
            sx={{
              py: 0.4,
              px: 0.7,
              borderRadius: 15,
              backgroundColor: shouldShowBlue
                ? 'green.700'
                : isFuture
                  ? 'grey.400'
                  : startIndex === currentDomainIndex
                    ? 'blue.700'
                    : 'grey.400',
              borderWidth: shouldShowBlue ? 1 : 3,
              borderStyle: 'solid',
              borderColor: shouldShowBlue ? 'green.700' : shouldShowGrey ? 'grey.400' : 'blue.700',
              color: shouldShowBlue ? 'common.white' : 'black.500',
              cursor: 'pointer',
              display: 'flex',
              height: { xs: 26, sm: 28, md: 30 },
              minHeight: { xs: 26, sm: 28, md: 30 },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              minWidth: 100,
              maxWidth: 110,
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            <Typography
              variant="textXS_regular"
              sx={{
                fontSize: '0.75rem',
                color: shouldShowBlue ? 'common.white' : 'black.500',
                whiteSpace: 'nowrap',
              }}
            >
              {domainCount}{' '}
              {domainCount === 1 ? (
                <Trans i18nKey="assessments.new.progressFlow.domain" />
              ) : (
                <Trans i18nKey="assessments.new.progressFlow.domains" />
              )}
              {!shouldShowBlue && startIndex <= currentDomainIndex && domainCount > 2 && ' more'}
            </Typography>
            <CaretRightIcon
              size={16}
              style={{
                transform: isExpanded
                  ? isRtl
                    ? 'rotate(-90deg) scaleX(-1)'
                    : 'rotate(90deg)'
                  : isRtl
                    ? 'scaleX(-1)'
                    : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </Box>

          <Collapse in={isExpanded}>
            <Paper
              elevation={2}
              sx={{
                position: 'absolute',
                left: isRtl ? 'auto' : '50%',
                right: isRtl ? '50%' : 'auto',
                transform: isRtl ? 'translateX(50%)' : 'translateX(-50%)',
                zIndex: 1,
                mt: 1,
                minWidth: 200,
                maxWidth: 300,
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: 1,
              }}
            >
              <Box
                sx={{
                  p: 1,
                  ...(domains.length > 7 && {
                    maxHeight: 7 * 44,
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(0,0,0,0.15) transparent',
                    '&::-webkit-scrollbar': { width: 5 },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 999 },
                    '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.25)' },
                  }),
                }}
              >
                {domains.map((domain, idx) => {
                  const domainIndex = startIndex + idx;
                  const domainState = domainStates.get(domainIndex);
                  const isDomainCompleted = domainState === AssessmentDomainStatus.COMPLETED;
                  const isDomainCurrent = domainState === AssessmentDomainStatus.IN_PROGRESS;
                  const isDomainSelected =
                    selectedIndex !== undefined && domainIndex === selectedIndex;
                  const isClickable =
                    (isDomainCompleted || isDomainCurrent) && onNodeClick && !isNavigationDisabled;
                  // Only make bold if selected. If progress domain is not selected, it should not be bold

                  return (
                    <Box
                      key={domain.id}
                      onClick={
                        isClickable
                          ? () => {
                            onNodeClick?.(domainIndex);
                            onToggle();
                          }
                          : undefined
                      }
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: isDomainCurrent ? 'blue.100' : 'transparent',
                        borderWidth: 1,
                        borderStyle: isDomainCurrent ? 'dashed' : 'solid',
                        borderColor: isDomainCurrent ? 'blue.700' : 'transparent',
                        cursor: isClickable ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: isClickable ? 'grey.200' : 'transparent',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isDomainCompleted
                            ? 'green.700'
                            : isDomainCurrent
                              ? 'blue.700'
                              : 'grey.400',
                          color:
                            isDomainCompleted || isDomainCurrent ? 'common.white' : 'black.500',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {isDomainCompleted ? <CheckIcon size={14} weight="bold" /> : domainIndex}
                      </Box>
                      <Typography
                        variant="textSM_medium"
                        sx={{
                          flex: 1,
                          color: isDomainSelected ? 'black.1400' : 'black.200',
                          fontWeight: isDomainSelected ? 600 : 500, // Match regular domains - only selected is bold
                          textAlign: isRtl ? 'right' : 'left',
                        }}
                      >
                        {domain.title}
                      </Typography>
                      {domain.isOptional && (
                        <Badge
                          variant="Optional"
                          label={<Trans i18nKey="assessments.new.progressFlow.optional" />}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Collapse>
        </Box>
      </ClickAwayListener>
    );
  },
);
